import { Grid } from "@mui/material";
import { Observer, observer } from "mobx-react";
import { EvaluationStatus } from "models/Evaluation";
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { Scrollbars } from "react-custom-scrollbars-2";
import { EvaluationService } from "services/EvaluationService";
import { AuthStore } from "stores/AuthStore";
import { useStore } from "utils/useStore";
import { InteractionType } from "../../../models/InteractionType";
import { useResizeObserver } from "../../../utils/useResizeObserver";
import MessageStore from "../../ManagerInteractions/Stores/MessageStore";
import AcxLoadingIndicator from "../../UI/AcxLoadingIndicator";
import AcxSnackBar from "../../UI/AcxSnackBar";
import ErrorSnackBar from "../../UI/ErrorSnackBar";
import {
    ClassifierValidationModuleTypeName,
    EvalLicensedModule,
    EvalStore,
} from "../Stores/EvalStore";
import ClassifierResultFeedbackCard from "./ClassifierFeedback/ClassifierResultFeedbackCard";
import ClipList from "./Clips/ClipList";
import ConvertToHumanEvalDialog from "./Dialogs/ConvertToHumanEvalDialog";
import NoActionThreadsFoundDialog from "./Dialogs/NoActionThreadsFoundDialog";
import ReassignHierarchyDialog from "./Dialogs/ReassignHierarchyDialog";
import RequestDisputeDialog from "./Dialogs/RequestDisputeDialog";
import RequestReviewActionDialog from "./Dialogs/RequestReviewActionDialog";
import SaveEvaluationDialog from "./Dialogs/SaveEvaluationDialog";
import EvalNav from "./EvalNav";
import InteractionMeta from "./InteractionMeta";
import InteractionMetadata from "./InteractionMetadata";
import MediaViewerDock from "./MediaViewerDock";
import ModuleCard from "./Modules/ModuleCard";
import ReviewRequestDetailsCard from "./Modules/ReviewDetails/ReviewRequestDetailsCard";
import SearchingForNextEvalDialog from "./Dialogs/SearchingForNextEvalDialog";
import CreateNewAgentDialog from "components/Admin/Organizations/OrganizationDetail/OrganizationAgents/OrganizationAgentDetails/CreateNewAgentDialog";
import SideControlsPage from "Layouts/SideControls/SideControlsPage";
import { MAIN_APP_BAR_HEIGHT_PX } from "components/Navigation/appBarHeight";

const textMediaDockMinSize = 233;
const textMediaDockMaxSize = 545;

interface AnalystTimeSpentTrackerProps {
    evaluationId: string;
    setIsCurrentUserAnalyst: React.Dispatch<React.SetStateAction<boolean>>;
    navigateBack: () => void;
}

const trackingIntervalSeconds = 30;

function AnalystTimeSpentTracker(props: AnalystTimeSpentTrackerProps) {
    const intervalHandleRef = useRef<NodeJS.Timeout | null>(null);
    const lastTimeRef = useRef<number | null>(null);

    const setIsCurrentUserAnalyst = props.setIsCurrentUserAnalyst;

    const evaluationService = useMemo(() => new EvaluationService(), []);

    const messageStore = useStore(MessageStore);

    const { evaluationId, navigateBack } = props;

    const stopTracking = useCallback(
        (noLongerAnalyst?: boolean) => {
            if (intervalHandleRef.current) {
                clearInterval(intervalHandleRef.current);
                intervalHandleRef.current = null;
            }

            // any leftover time not hit by the interval is sent along
            // unless the user is no longer the analyst, in which case we skip so as to not hit the endpoint again
            if (lastTimeRef.current && !noLongerAnalyst) {
                const leftoverTimeInMs = Date.now() - lastTimeRef.current;
                const leftoverTimeInSeconds = Math.floor(
                    leftoverTimeInMs / 1000,
                );
                lastTimeRef.current = null;

                if (leftoverTimeInSeconds <= 0) return;

                evaluationService.addAnalystTimeSpent(
                    evaluationId,
                    leftoverTimeInSeconds,
                );
            }
        },
        [evaluationId, evaluationService],
    );

    const startTracking = useCallback(() => {
        if (intervalHandleRef.current) return;

        lastTimeRef.current = Date.now();
        intervalHandleRef.current = setInterval(async () => {
            lastTimeRef.current = Date.now();
            const res = await evaluationService.addAnalystTimeSpent(
                evaluationId,
                trackingIntervalSeconds,
            );
            if (res.status === 205) {
                // 205 is ResetContent, if we receive this the request *worked* but the current user is no longer the analyst so we should set that to false
                // Doing that will also stop tracking, since the component will unmount itself
                stopTracking();
                setIsCurrentUserAnalyst(false);
                // Show a message warning the user another analyst was assigned and prompting them to go back to My Evaluations
                messageStore.logMessage(
                    "Another analyst has been assigned to this evaluation. Please start a new evaluation.",
                    "info",
                    {
                        buttonAction: {
                            approve: () => {
                                messageStore.showNextMessage();
                                navigateBack();
                            },
                            deny: () => {},
                        },
                        buttonText: {
                            approve: "Back to My Evaluations",
                            deny: "",
                        },
                        hideButton: { approve: false, deny: true },
                    },
                );
            }
        }, trackingIntervalSeconds * 1000);
    }, [
        evaluationId,
        evaluationService,
        setIsCurrentUserAnalyst,
        stopTracking,
        messageStore,
        navigateBack,
    ]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden) {
                stopTracking();
            } else {
                startTracking();
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("beforeunload", () => {
            stopTracking();
        });
        startTracking();

        return () => {
            stopTracking();

            document.removeEventListener(
                "visibilitychange",
                handleVisibilityChange,
            );
            window.removeEventListener("beforeunload", () => {
                stopTracking();
            });
        };
    }, [startTracking, stopTracking]);

    return null;
}

const EvalPage = observer((props: { navigateBack: () => void }) => {
    const store = useStore(EvalStore);
    const messageStore = useStore(MessageStore);
    const authStore = useStore(AuthStore);
    const [isCurrentUserAnalyst, setIsCurrentUserAnalyst] = useState(false);
    const [isInteractionMetaClosed, setIsInteractionMetaClosed] =
        useState(false);

    useEffect(() => {
        store.showInteractionMetadata = false;
    }, [store]);

    useEffect(() => {
        if (!store.currentEval) return setIsCurrentUserAnalyst(false);

        authStore.getUserObject().then((user) => {
            if (!store.currentEval || !user)
                return setIsCurrentUserAnalyst(false);

            setIsCurrentUserAnalyst(
                store.currentEval.analystId === user.profile.uId,
            );
        });
    }, [store.currentEval, authStore]);

    const {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        elementHeight: centerHeight,
        elementWidth: centerWidth,
        theRef: centerRef,
    } = useResizeObserver<HTMLDivElement>({
        name: "EvalCenterView",
        pixelDelta: 1,
    });

    const {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        elementHeight: leftHeight,
        elementWidth: leftWidth,
        theRef: leftRef,
    } = useResizeObserver<HTMLDivElement>({
        name: "EvalLeftPanelView",
        pixelDelta: 1,
    });

    const canSeeTranscript = authStore.canUserView("Transcription");

    let avMediaDockMinSize = 80;
    let avMediaDockMaxSize = 195;

    if (canSeeTranscript) {
        avMediaDockMinSize = 195;
        avMediaDockMaxSize = 545;
    }

    return (
        <Observer>
            {() => (
                <>
                    {store.currentEval &&
                        store.currentEval.evaluationStatus ===
                            EvaluationStatus.InProgress &&
                        isCurrentUserAnalyst && (
                            <AnalystTimeSpentTracker
                                setIsCurrentUserAnalyst={
                                    setIsCurrentUserAnalyst
                                }
                                evaluationId={store.currentEval.id}
                                navigateBack={props.navigateBack}
                            />
                        )}
                    <CreateNewAgentDialog
                        isOpen={store.createAgentDialogStore.isOpen}
                        onClose={store.createAgentDialogStore.close}
                        onSubmitHandler={store.onAgentCreateConfirm}
                        isLoading={store.getTaskLoading("Create Agent")}
                        orgId={store.organizationId}
                    />
                    <EvalNav
                        evalNumber={store.currentEval?.evaluationQbId ?? ""}
                        navigateBack={props.navigateBack}
                        moduleDisplayNames={store.evaluationModuleNames}
                    />
                    <AcxSnackBar
                        open={messageStore.showMessage}
                        onClose={messageStore.showNextMessage}
                        message={messageStore.currentMessage?.text || ""}
                        severity={messageStore.currentMessage?.severity}
                        options={messageStore.currentMessage?.options}
                    />
                    {store.noActionsFoundDialogStore.isOpen && (
                        <NoActionThreadsFoundDialog
                            dialogStore={store.noActionsFoundDialogStore}
                        />
                    )}
                    {store.saveEvalDialogStore.isOpen && (
                        <SaveEvaluationDialog
                            dialogStore={store.saveEvalDialogStore}
                        />
                    )}
                    {store.searchingForNextEvalDialogStore.isOpen && (
                        <SearchingForNextEvalDialog
                            dialogStore={store.searchingForNextEvalDialogStore}
                        />
                    )}
                    {store.requestActionDialogStore.isOpen && (
                        <RequestReviewActionDialog
                            navigateBack={props.navigateBack}
                            dialogStore={store.requestActionDialogStore}
                        />
                    )}
                    {store.requestDisputeDialogStore.isOpen && (
                        <RequestDisputeDialog evalStore={store} />
                    )}
                    {store.reassignHierarchyDialogStore.isOpen && (
                        <ReassignHierarchyDialog evalStore={store} />
                    )}
                    <ConvertToHumanEvalDialog evalStore={store} />
                    <ErrorSnackBar
                        store={store}
                        onClose={store.clearLastTaskError}
                    />
                    <SideControlsPage
                        enableMinimize
                        drawerLocation="left"
                        drawerMaxWidth={480}
                        mainGridSpacing={0}
                        mainGridJustify={"flex-start"}
                        mainGridAlignItems={"stretch"}
                        containerOffsetPixels={MAIN_APP_BAR_HEIGHT_PX}
                        drawerVerticalOffsetPixels={0}
                        onDrawerChange={setIsInteractionMetaClosed}
                        drawerClosed={isInteractionMetaClosed}
                        drawerSx={{ zIndex: 1097 }}
                        toggleButtonStyles={
                            isTextMediaType(store.mediaType)
                                ? { zIndex: 9999 }
                                : {}
                        }
                        mainSx={{
                            minWidth: "815px",
                            width: "100%",
                            height: "100%",
                            paddingTop: "0px",
                            paddingRight: "0px",
                        }}
                        drawerContent={
                            <Grid
                                ref={leftRef}
                                container
                                item
                                sx={{ height: "100%", width: "100%" }}
                            >
                                <InteractionMeta
                                    drawerPixelOffset={store.mediaDrawerHeight}
                                    interactionType={store.mediaType}
                                    rightOffset={20}
                                />
                            </Grid>
                        }
                        mainContent={
                            <Grid
                                container
                                item
                                xs={12}
                                direction={"row"}
                                sx={{
                                    height: "100%",
                                    width: "100%",
                                    minWidth: "815px",
                                }}
                            >
                                <Grid
                                    ref={centerRef}
                                    item
                                    xs={
                                        (store.currentEval?.interaction
                                            ?.interactionType !== 4 &&
                                            (store.authStore.isUserUltra() ||
                                                !store.authStore.canUserView(
                                                    "Restrict Media",
                                                ))) ||
                                        store.showInteractionMetadata
                                            ? 9
                                            : 12
                                    }
                                    sx={{ height: "100%", width: "100%", marginTop: "16px" }}
                                >
                                    <div
                                        style={{
                                            paddingLeft: "45px",
                                            paddingRight: "45px",
                                            paddingTop: "6px",
                                            height: `calc(100% -  ${store.mediaDrawerHeight}px)`,
                                            width: "100%",
                                        }}
                                    >
                                        {store.evalAndDependenciesLoading ? (
                                            <AcxLoadingIndicator
                                                color={"secondary"}
                                                alternate={"PuffLoader"}
                                                size={175}
                                            />
                                        ) : (
                                            <>
                                                {Boolean(
                                                    store.currentModule,
                                                ) && (
                                                    <Scrollbars autoHide>
                                                        {store.showReviewDetailsCard && (
                                                            <ReviewRequestDetailsCard
                                                                enableComments={
                                                                    false
                                                                }
                                                                navigateBack={
                                                                    props.navigateBack
                                                                }
                                                                {...store.reviewDetailsCardProps}
                                                            />
                                                        )}
                                                        <ModuleCard
                                                            module={
                                                                store.currentModule as EvalLicensedModule
                                                            }
                                                        />
                                                    </Scrollbars>
                                                )}
                                                {store.currentDynamicModule ===
                                                    ClassifierValidationModuleTypeName &&
                                                    store.classifierValidationEnabled && (
                                                        <Scrollbars autoHide>
                                                            <ClassifierResultFeedbackCard />
                                                        </Scrollbars>
                                                    )}
                                            </>
                                        )}
                                    </div>
                                </Grid>
                                {store.showInteractionMetadata && (
                                    <Grid
                                        container
                                        item
                                        xs={3}
                                        sx={{ height: "100%", width: "100%" }}
                                        justifyContent="center"
                                    >
                                        <InteractionMetadata
                                            drawerPixelOffset={
                                                store.mediaDrawerHeight
                                            }
                                            interactionType={store.mediaType}
                                        />
                                    </Grid>
                                )}
                                {!store.showInteractionMetadata &&
                                    store.currentEval?.interaction
                                        ?.interactionType !== 4 &&
                                    (store.authStore.isUserUltra() ||
                                        !store.authStore.canUserView(
                                            "Restrict Media",
                                        )) && (
                                        <Grid
                                            container
                                            item
                                            xs={3}
                                            sx={{
                                                height: "100%",
                                                width: "100%",
                                            }}
                                            justifyContent="center"
                                        >
                                            <ClipList
                                                drawerPixelOffset={store.mediaDrawerHeight}
                                                interactionType={
                                                    store.mediaType
                                                }
                                            />
                                        </Grid>
                                    )}
                            </Grid>
                        }
                    />

                    {Boolean(store.mediaType !== undefined) &&
                        (store.authStore.isUserUltra() ||
                            !store.authStore.canUserView("Restrict Media")) && (
                            <>
                                <MediaViewerDock
                                    miniHeight={
                                        isTextMediaType(store.mediaType)
                                            ? textMediaDockMinSize
                                            : avMediaDockMinSize
                                    }
                                    maxHeight={
                                        isTextMediaType(store.mediaType)
                                            ? textMediaDockMaxSize
                                            : avMediaDockMaxSize
                                    }
                                    altLeftPosition={
                                        isTextMediaType(store.mediaType)
                                            ? isInteractionMetaClosed
                                                ? 0
                                                : leftWidth
                                            : undefined
                                    }
                                    altWidth={
                                        isTextMediaType(store.mediaType)
                                            ? centerWidth
                                            : undefined
                                    }
                                    store={store}
                                    canSeeTranscript={canSeeTranscript}
                                    collapsible
                                />
                            </>
                        )}
                </>
            )}
        </Observer>
    );
});

export function isTextMediaType(mediaType?: InteractionType) {
    return (
        mediaType === InteractionType.ProcessedChat ||
        mediaType === InteractionType.Email
    );
}

export default EvalPage;
